import CloseIcon from '@mui/icons-material/Close';
import {
	Box,
	Container,
	IconButton,
	Stack,
	Typography,
	Grid,
	FormControl,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ButtonComp from 'common/components/Button/ButtonComp';
import MultipleSelectCheckmarks from 'common/components/MulltipleSelectCheckmarks/MultipleSelectCheckmarks';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	ASSIGN_ROLES_LABEL,
	USER_CONTACT_INFO,
	ADD_USER_SERVICE_UNAVAILABLE_ERROR,
	EXISTING_USER_EMAIL_ERROR_LABEL,
	USER_ID_SUCCESSFULLY_ADDED_LABEL,
	USER_ID_INCLUDES_LABEL,
	LAST_NAME,
	FIRST_NAME,
	ALL_FIELDS_VALIDATION_ERROR,
	EMAIL,
} from 'common/constants/index';
import { APP_GENERAL_ERROR_MSG } from 'common/constants/TextContent';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import { toSentenceCase, validateEmail } from 'common/utils/utilFunctions';

import { getRolesTableData } from 'features/Settings/Permission/api/index';
import {
	IRolesApiResponse,
	IRole,
	IUserRole,
} from 'features/Settings/Permission/api/type-def';

import { createUser } from '../api/index';
import { IAddUserParams, IAddUserListFormProps } from '../api/type-def';

import ModalEle from '../../../../common/components/Modal/ModalEle';
import TextFieldComponent from '../../../../common/components/TextField/TextField';
import { regex } from '../../../../common/constants/TextContent';
import { UserStatus } from '../../../../common/constants/UserManagementConstants';

import styles from './CreateUser.module.scss';

interface ICreateUserModalProps {
	onClose: () => void;
	openModal: boolean;
	isAdd?: boolean;
}
const CreateUser = (props: ICreateUserModalProps) => {
	const { onClose, openModal } = props;
	const { BAD_REQUEST } = HTTP_STATUS_CODES;
	const { ACTIVE } = UserStatus;
	const [isLoading, setLoading] = useState<boolean>(false);
	const [openDialog, setDialog] = useState<boolean>(openModal);
	const dispatch = useDispatch();
	const [rolesData, setRolesData] = useState<IUserRole[]>([]);
	const [, setSelectedRolesData] = useState<IUserRole[]>([]);
	const defaultAddformValues: IAddUserListFormProps = {
		firstName: '',
		lastName: '',
		email: '',
		status: ACTIVE,
		roles: [],
	};

	const {
		control,
		reset,
		getValues,
		formState: { isDirty, isValid },
	} = useForm<IAddUserListFormProps>({
		mode: 'onChange',
		defaultValues: defaultAddformValues,
	});

	const { t } = useTranslation();
	const [formData, setFormData] = useState<any>({
		roles: [],
	});

	const handleModalClose = useCallback(() => {
		setDialog(false);
		onClose();
	}, []);

	const saveUser = async () => {
		setLoading(true);
		const addUserFormvalues = getValues();
		const allRoles: any = [];
		if (formData?.roles?.length > 0) {
			formData?.roles.forEach((role: any) => {
				role.roleName = role?.label;
				const roleObj = {
					roleId: role?.id,
				};
				allRoles.push(roleObj);
			});
		}
		addUserFormvalues.roles = allRoles;

		const payload: IAddUserParams = {
			...buildBasePayload(addUserFormvalues),
		};

		const response: any = await createUser(payload);
		if (response?.responseBody) {
			reset(defaultAddformValues);
			setLoading(false);
			dispatch(
				setToastMessage({
					message: USER_ID_SUCCESSFULLY_ADDED_LABEL,
					severity: 'success',
					isToastOpen: true,
				}),
			);
			onClose();
		} else if (
			response?.statusCode === BAD_REQUEST &&
			response?.errorBody?.errorMessage?.includes(USER_ID_INCLUDES_LABEL)
		) {
			setLoading(false);
			dispatch(
				setToastMessage({
					message: EXISTING_USER_EMAIL_ERROR_LABEL,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		} else {
			setLoading(false);
			dispatch(
				setToastMessage({
					message: ADD_USER_SERVICE_UNAVAILABLE_ERROR,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const getDisabledStatus = () => {
		let isDisabled = false;
		if (!isDirty || !isValid) {
			isDisabled = true;
		}
		return isDisabled;
	};

	/**
	 * Called on AddUser
	 * Call api to add details added on screen
	 * closes modal on sucess
	 * show toast message
	 */
	const onAddUser = () => {
		saveUser();
	};

	/**
	 *
	 * @param data formdata
	 * sets the params
	 * @returns paramObj
	 */
	const buildBasePayload = (data: IAddUserListFormProps) => {
		const paramsObj: IAddUserParams = {} as IAddUserParams;

		paramsObj.firstName = data?.firstName;
		paramsObj.lastName = data?.lastName;
		paramsObj.email = data?.email;
		paramsObj.status = data?.status;
		paramsObj.userName = data?.email;
		paramsObj.roles = data?.roles;
		paramsObj.cellPhone = USER_CONTACT_INFO;

		return paramsObj;
	};
	/**
	 *
	 * @param data GroupInfo
	 * delete the selected group
	 */

	const fetchRolesTableData = async () => {
		const data = {
			pageSize: 500,
			pageNumber: 0,
		};
		try {
			const response = await getRolesTableData(data);
			if (response?.responseBody) {
				const responseData: IRolesApiResponse = response.responseBody;
				const tempTableData = responseData?.content || [];
				if (tempTableData?.length > 0) {
					const rolesTableData = tempTableData.map((role: IRole) => ({
						id: role?.roleId,
						label: role?.roleName,
						value: role?.roleId,
						checked: false,
					}));

					setRolesData(rolesTableData);
				} else {
					setRolesData([]);
				}
			}
		} catch (error) {
			setLoading(false);
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const onRoleSelection = (data: any) => {
		const field = 'roles';
		setSelectedRolesData(data);
		setFormData((prevFormData: any) => ({
			...prevFormData,
			[field]: data,
		}));
	};
	useEffect(() => {
		fetchRolesTableData();
	}, []);
	return (
		<div data-testid="add-user-main-div">
			<ModalEle open={openDialog} handleClose={handleModalClose}>
				<Container className={styles['add-user']}>
					<Stack direction="row" justifyContent="space-between">
						<Typography className={styles['add-user-label']}>
							{t('add_user_label')}
						</Typography>
						<Stack direction="row" justifyContent="end">
							<IconButton
								onClick={handleModalClose}
								data-testid="onmodal-close"
							>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Stack>
					</Stack>

					<Box>
						<Stack>
							<Typography className={styles['add-usr-desc']}>
								{t('add_user_description')}
							</Typography>
							<Typography className={styles['add-err-desc']}>
								{ALL_FIELDS_VALIDATION_ERROR}
							</Typography>
						</Stack>

						<div className={styles['createuser-field-container']}>
							<form>
								<Stack direction="row" spacing={2}>
									<Stack
										direction="column"
										spacing={1}
										className={styles['field-size']}
									>
										<Typography
											align="left"
											className={styles['first-name-style']}
										>
											{FIRST_NAME}
										</Typography>
										<Controller
											name="firstName"
											control={control}
											data-testid="first-name"
											rules={{
												required: {
													value: true,
													message: 'First Name Required',
												},
												pattern: {
													value: regex,
													message: 'Special characters not allowed',
												},
											}}
											render={({
												field: { name, value, onChange },
												fieldState: { error },
											}) => (
												<TextFieldComponent
													fullWidth
													value={value}
													inputProps={{
														tabIndex: 1,
														'data-testid': 'first-name',
													}}
													type="text"
													onChange={onChange}
													name={name}
													error={!!error}
													helperText={error ? error.message : null}
												/>
											)}
										/>
									</Stack>
									<Stack
										direction="column"
										spacing={1}
										className={styles['field-size']}
									>
										<Typography
											align="left"
											className={styles['first-name-style']}
										>
											{LAST_NAME}
										</Typography>

										<Controller
											name="lastName"
											control={control}
											rules={{
												required: {
													value: true,
													message: 'Last Name Required',
												},
												pattern: {
													value: regex,
													message: 'Special characters not allowed',
												},
											}}
											render={({
												field: { name, value, onChange },
												fieldState: { error },
											}) => (
												<TextFieldComponent
													fullWidth
													inputProps={{
														tabIndex: 2,
														'data-testid': 'last-name',
													}}
													type="text"
													onChange={onChange}
													name={name}
													value={value}
													error={!!error}
													helperText={error ? error.message : null}
												/>
											)}
										/>
									</Stack>
								</Stack>

								<Stack>
									<Typography
										align="left"
										className={styles['first-name-style']}
									>
										{EMAIL}
									</Typography>
									<Grid item xs={12} sm={12} md={12} xl={12} lg={12} pt={1}>
										<Controller
											name="email"
											control={control}
											rules={{ required: true, validate: validateEmail }}
											render={({
												field: { name, value, onChange },
												fieldState: { error },
											}) => (
												<TextFieldComponent
													fullWidth
													inputProps={{
														tabIndex: 2,
														'data-testid': 'user-email',
													}}
													type="email"
													onChange={onChange}
													name={name}
													value={value}
													error={!!error}
													helperText={error ? error.message : null}
												/>
											)}
										/>
									</Grid>
								</Stack>
								<Stack pt={1}>
									<div className={styles['graph-action']}>
										<Grid container>
											<Grid item xs={12} sm={12} md={12} xl={12} lg={12}>
												<FormControl className={styles['graph-container']}>
													<Typography className={styles['graph-action-lebel']}>
														{ASSIGN_ROLES_LABEL}
													</Typography>
													<MultipleSelectCheckmarks
														className={styles['graph-action-dropdown']}
														isClearFilter={false}
														isSearchBox
														clearable
														inputLabel=""
														optionsProps={rolesData}
														onSelectedChange={onRoleSelection}
														onClearBtnClick
														isInputLabel={false}
													/>
												</FormControl>
											</Grid>
										</Grid>
									</div>
								</Stack>

								<Stack
									direction="row"
									className={styles['btn-grp-add-user']}
									spacing={2}
								>
									{/* <ButtonComp
										btnType="secondary"
										clickHandler={onSaveAndReset}
										disabled={!isDirty || !isValid}
										compClass={styles['btn-grp-save']}
										data-testid="add-user-on-reset"
									>
										{t('save_more')}
									</ButtonComp> */}
									<ButtonComp
										btnType="primary"
										size="medium"
										clickHandler={onAddUser}
										disabled={getDisabledStatus()}
										compClass={styles['btn-grp-add']}
										data-testid="add-user-on-save"
										loading={isLoading}
									>
										{toSentenceCase('Add User')}
									</ButtonComp>
								</Stack>
							</form>
						</div>
					</Box>
				</Container>
			</ModalEle>
		</div>
	);
};

export default CreateUser;
